import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import ChangeEmailDialog from "@/components/ChangeEmailDialog";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";
import { SchoolCalendar } from "@/components/calendar/SchoolCalendar";
import { CourseCalendar } from "@/components/calendar/CourseCalendar";
import { CalendarDays, UserRound, LogOut, Lock } from "lucide-react";
import { toast } from "sonner";

const LOGO = "https://www.areteteatro.com/wp-content/uploads/2020/03/logo-arete.png";

// Sezioni dell'Area docente: per aggiungerne una nuova basta inserirla qui
const SECTIONS = [
  { key: "calendario", label: "Calendario", icon: CalendarDays, Comp: () => <CalendarSection /> },
  { key: "profilo", label: "Profilo", icon: UserRound, Comp: () => <ProfileSection /> },
];

export default function TeacherArea() {
  const { user, logout } = useAuth();
  const [params, setParams] = useSearchParams();
  const active = SECTIONS.find((s) => s.key === params.get("sezione")) || SECTIONS[0];
  const go = (key) => setParams({ sezione: key });

  return (
    <div className="min-h-screen bg-background" data-testid="teacher-area">
      <header style={{ paddingTop: "env(safe-area-inset-top)" }} className="sticky top-0 z-30 bg-black/80 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2.5 min-w-0"><img src={LOGO} alt="Areté Teatro" className="h-9 w-9 rounded-full bg-white p-0.5 object-contain shrink-0" /><span className="text-lg font-display text-white whitespace-nowrap">Areté <span className="text-primary">Docenti</span></span></div>
          <div className="flex items-center gap-1 sm:gap-3">
            <span className="text-sm text-white/70 hidden md:block">Ciao, {user?.name?.split(" ")[0]}</span>
            <Button data-testid="logout-button" variant="ghost" size="sm" onClick={logout} className="text-white hover:bg-white/10 hover:text-white"><LogOut size={16} className="sm:mr-1" /> <span className="hidden sm:inline">Esci</span></Button>
          </div>
        </div>
        <nav className="max-w-6xl mx-auto px-4 sm:px-6 flex gap-1 overflow-x-auto pb-2" data-testid="teacher-menu">
          {SECTIONS.map(({ key, label, icon: Icon }) => (
            <button key={key} type="button" data-testid={`teacher-menu-${key}`} onClick={() => go(key)} aria-current={active.key === key ? "page" : undefined}
              className={`shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-colors ${active.key === key ? "bg-primary text-primary-foreground" : "text-white/80 hover:bg-white/10"}`}>
              <Icon size={15} /> {label}
            </button>
          ))}
        </nav>
      </header>
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 fade-up" data-testid={`teacher-section-${active.key}`}>
        <active.Comp />
      </main>
    </div>
  );
}

function CalendarSection() {
  const { user } = useAuth();
  const [yrs, setYrs] = useState(null);
  const [year, setYear] = useState(null);
  const [metas, setMetas] = useState([]);

  useEffect(() => {
    api.get("/staff/years").then((r) => { setYrs(r.data); setYear(r.data.current_year); })
      .catch((err) => toast.error(apiError(err.response?.data?.detail)));
  }, []);
  useEffect(() => {
    if (!year) return;
    api.get("/staff/course-meta", { params: { year } }).then((r) => setMetas(r.data)).catch(() => setMetas([]));
  }, [year]);

  if (!yrs || !year) return <p className="text-muted-foreground">Caricamento…</p>;
  const mine = metas.filter((m) => m.teacher_user_id === user?.user_id);
  const onMeta = (m) => setMetas((ms) => ms.map((x) => (x.course === m.course ? m : x)));

  return (
    <div className="space-y-8">
      <section className="space-y-3" data-testid="teacher-my-courses">
        <h2 className="text-2xl">I miei corsi <span className="text-base text-muted-foreground">· {year}</span></h2>
        {mine.length === 0
          ? <Card className="p-5 text-sm text-muted-foreground" data-testid="teacher-no-courses">Nessun corso assegnato a te per quest'anno. Puoi consultare il calendario della scuola qui sotto.</Card>
          : mine.map((m) => (
            <div key={m.course}>
              <p className="text-lg font-semibold break-words mb-2 flex flex-wrap items-center gap-2" data-testid={`teacher-course-${m.course}`}>{m.course}
                {m.calendar_locked
                  ? <span className="text-xs font-medium inline-flex items-center gap-1 rounded-full px-2.5 py-1 bg-foreground text-background"><Lock size={12} /> Calendario confermato e bloccato</span>
                  : <span className="text-xs font-medium rounded-full px-2.5 py-1 border border-border text-muted-foreground">Modificabile</span>}
              </p>
              <CourseCalendar key={`${year}|${m.course}`} year={year} course={m.course} onMeta={onMeta} />
            </div>
          ))}
      </section>
      <section className="space-y-3">
        <h2 className="text-2xl">Calendario della scuola</h2>
        <p className="text-sm text-muted-foreground">Lezioni di tutti i corsi. Puoi modificare solo quelle dei tuoi corsi non bloccati.</p>
        <SchoolCalendar key={`${year}|${mine.map((m) => `${m.course}${m.calendar_locked}`).join(",")}`} year={year} years={yrs.years} currentYear={yrs.current_year} onYear={setYear} />
      </section>
    </div>
  );
}

function ProfileSection() {
  const { user } = useAuth();
  return (
    <Card className="p-6 space-y-5 max-w-xl" data-testid="teacher-profile">
      <h2 className="text-2xl">Profilo</h2>
      <div><p className="text-xs text-muted-foreground">Nome</p><p className="text-lg text-foreground break-words" data-testid="teacher-profile-name">{user?.name}</p></div>
      <div><p className="text-xs text-muted-foreground">Email</p><p className="text-lg text-foreground break-all" data-testid="teacher-profile-email">{user?.email}</p></div>
      <div className="flex flex-wrap gap-2">
        <ChangeEmailDialog className="border border-border" />
        <ChangePasswordDialog className="border border-border" />
      </div>
    </Card>
  );
}
